import {
  CommonHardwareProductDetailsFragment,
  AlternativeModelSectionFragment,
  BundlesSectionFragment,
  ShopFeaturesFragment,
  RichTextFragment,
  ProductVariantComponentsFragment,
  HardwareProductDetailsVariantFragment,
  TileFragmentFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import type { ICartState } from '@/ui/store/cart/types/ICartState';
import { Bundle } from '@/ui/services/BundleService';
import { ColorDetail } from '@/ui/components/pages/ProductDetail/types/ColorDetail';
import { AttributeTileDetail } from '@/ui/components/pages/ProductDetail/types/attribute-tile-detail';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { parsePriceVariant } from '@/ui/utils/parsePriceVariant';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import {
  DEFAULT_MAX_DELIVERY_TIME_FALLBACK,
  DEFAULT_MIN_DELIVERY_TIME_FALLBACK,
} from '@/utils/constants';
import { DeliveryTimeRange } from '@/integrations/crystallize/types/localization/DeliveryTimeRange';
import {
  extractTopicPaths,
  parseColorDetail,
  parseRelationTileDetail,
} from '@/integrations/crystallize/helpers/product-variant-helper';
import {
  parseRestrictedDisplayMode,
  RestrictedDisplayMode,
} from '@/utils/tile-country-visibility';
import { ParsedHardwareProductData } from '../components/pages/ProductDetail/types/ParsedHardwareProductData';
import { ProductAlternativeModel } from '../components/pages/ProductDetail/types/ProductAlternativeModel';
import { ParsedShopFeatures } from '../components/pages/ProductDetail/SmartlockDetail/types/ParsedShopFeatures';
import { ParsedHardwareProductVariant } from '../components/pages/ProductDetail/types/ParsedHardwareProductVariant';
import { BaseProduct } from './BaseProductService';
import { isPhysicalProductVariantInStock, isProductLowInStock } from './utils/isVariantInStock';
import { ParsedProductVariant } from '../components/pages/ProductDetail/types/ParsedProductVariant';

// Boundary shape for the country-restriction display mode added to the GraphQL
// fragments (the `country-restriction-display-mode` component). The accessor
// takes `unknown` and asserts this shape internally so a generated item type
// that does not yet expose the field (i.e. before `graphql-codegen` is re-run)
// still compiles — the parsing simply fails open until the data actually lands.
interface ItemWithRestrictionDisplayMode {
  restrictionDisplayMode?: {
    content?: {
      __typename?: string | null;
      options?: ({ value?: string | null } | null)[] | null;
    } | null;
  } | null;
}

function extractAliasedDisplayMode(item: unknown): RestrictedDisplayMode | undefined {
  const content = (item as ItemWithRestrictionDisplayMode).restrictionDisplayMode?.content;

  if (content?.__typename !== 'SelectionContent') {
    return undefined;
  }

  const value = content.options?.[0]?.value;

  return value ? parseRestrictedDisplayMode(value) : undefined;
}

export abstract class HardwareProduct<
    T extends CommonHardwareProductDetailsFragment = CommonHardwareProductDetailsFragment,
    V extends ParsedHardwareProductVariant = ParsedHardwareProductVariant,
  >
  extends BaseProduct<T, V>
  implements ParsedHardwareProductData {
  shortDescription?: RichTextFragment;

  alternativeModelHeading?: RichTextFragment;

  alternativeModelSection?: ProductAlternativeModel[];

  colorHeading?: RichTextFragment;

  bundlesHeading?: RichTextFragment;

  bundles: Bundle[] | null;

  availableColors: ColorDetail[];

  hideColorSelection: boolean;

  shopFeatures?: ParsedShopFeatures;

  constructor(data: T, cartStore: ICartState) {
    super(data, cartStore);
    this.parseProductVariant = this.parseProductVariant.bind(this);

    this.shortDescription =
      componentByTypename(data.shortDescription?.content, 'RichTextContent') || undefined;
    this.alternativeModelHeading =
      componentByTypename(data.alternativeModelHeading?.content, 'RichTextContent') || undefined;
    this.alternativeModelSection = this.parseAlternativeModels(data.alternativeModelSection);
    this.colorHeading =
      componentByTypename(data.colorHeading?.content, 'RichTextContent') || undefined;
    this.bundlesHeading =
      componentByTypename(data.bundlesText?.content, 'RichTextContent') || undefined;
    this.bundles = this.parseBundles(data.bundles);
    this.availableColors = this.extractUniqueColors(this.variants);
    this.hideColorSelection =
      componentByTypename(data.hideColorSelection?.content, 'BooleanContent')?.value || false;
    this.shopFeatures = this.parseShopFeatures(
      componentByTypename(data.shopFeatures?.content, 'ItemRelationsContent'),
    );

    this.cartStore = cartStore;
  }

  isSkuInStock(sku: string) {
    const productVariant = this.variants.find((variant) => variant.sku === sku);
    return isPhysicalProductVariantInStock(productVariant);
  }

  isVariantInStock(productVariant: ParsedProductVariant) {
    return isPhysicalProductVariantInStock(productVariant);
  }

  isLowStock(productVariant: ParsedProductVariant): boolean {
    return isProductLowInStock(productVariant);
  }

  getStandardDeliveryTimeRange(productVariant: ParsedProductVariant): DeliveryTimeRange {
    const { warehouseDetails } = productVariant;
    const min = warehouseDetails?.minimumDeliveryTime || DEFAULT_MIN_DELIVERY_TIME_FALLBACK;
    const max = warehouseDetails?.maximumDeliveryTime || DEFAULT_MAX_DELIVERY_TIME_FALLBACK;
    return { min, max };
  }

  getLowStockDeliveryTimeRange(productVariant: ParsedProductVariant): DeliveryTimeRange {
    const { warehouseDetails } = productVariant;
    const min = warehouseDetails?.fallbackMinimumDeliveryTime || DEFAULT_MIN_DELIVERY_TIME_FALLBACK;
    const max = warehouseDetails?.fallbackMaximumDeliveryTime || DEFAULT_MAX_DELIVERY_TIME_FALLBACK;
    return { min, max };
  }

  getDeliveryTimeRange(productVariant: ParsedProductVariant): DeliveryTimeRange {
    return this.isLowStock(productVariant)
      ? this.getLowStockDeliveryTimeRange(productVariant)
      : this.getStandardDeliveryTimeRange(productVariant);
  }

  get isAnyVariantInStock() {
    return this.variants.some((variant) => this.isVariantInStock(variant));
  }

  findAttachedBundleByExtRef(extRef: string): Bundle | undefined {
    return (this.bundles ?? []).find((bundle) => bundle?.documentExtRef === extRef);
  }

  abstract getSelectedVariant(options: { [key: string]: string | undefined }): V | undefined;

  protected parseProductVariant(
    rawVariant: HardwareProductDetailsVariantFragment,
  ): ParsedHardwareProductVariant {
    const baseVariant = super.parseProductVariant(rawVariant);
    return {
      ...baseVariant,
      color: this.parseColorDetails(rawVariant.components),
    };
  }

  protected parseBundles(
    bundleSectionData: BundlesSectionFragment | null | undefined,
  ): Bundle[] | null {
    const relatedBundleDocuments = componentByTypename(
      bundleSectionData?.content,
      'ItemRelationsContent',
    )?.items;

    if (!relatedBundleDocuments) {
      return null;
    }

    return relatedBundleDocuments
      .map((bundleData) => new Bundle(bundleData))
      .filter((bundle): bundle is Bundle => !!bundle);
  }

  private parseColorDetails(
    variantComponents?: ProductVariantComponentsFragment[] | null,
  ): ColorDetail | undefined {
    if (!variantComponents) {
      return undefined;
    }
    const colorContent = componentByTypename(
      variantComponents.find((c) => c.id === 'color')?.content,
      'SelectionContent',
    );
    return parseColorDetail(colorContent);
  }

  private parseAlternativeModels(
    alternativeSectionData: AlternativeModelSectionFragment | undefined | null,
  ): ProductAlternativeModel[] | undefined {
    const alternativeModelSection = componentByTypename(
      alternativeSectionData?.content,
      'ContentChunkContent',
    );

    if (!alternativeModelSection) {
      return undefined;
    }

    const productAlternativeModels = alternativeModelSection.chunks?.map((chunk) => {
      const description = componentByTypename(
        chunk?.find((component) => component.id === 'text')?.content,
        'RichTextContent',
      );
      const productItemRelation = componentByTypename(
        chunk?.find((component) => component.id === 'alternative-product')?.content,
        'ItemRelationsContent',
      );

      const firstProduct =
        productItemRelation?.items?.[0] && productItemRelation.items[0].__typename === 'Product'
          ? productItemRelation.items[0]
          : undefined;

      const firstProductVariant =
        firstProduct?.variants?.[0] && firstProduct.variants[0].__typename === 'ProductVariant'
          ? firstProduct.variants[0]
          : undefined;

      const additionalInformationText =
        componentByTypename(
          chunk.find((item) => item.id === 'additional-information-box')?.content,
          'RichTextContent',
        ) ?? undefined;

      const additionalInformationTiles = componentByTypename(
        chunk.find((item) => item.id === 'additional-information-tiles')?.content,
        'ItemRelationsContent',
      )?.items as TileFragmentFragment[];

      if (!firstProduct || !firstProductVariant) {
        return null;
      }

      return {
        path: firstProduct.path!,
        name: firstProduct.name!,
        image: firstProductVariant.firstImage || undefined,
        priceVariant: parsePriceVariant(firstProductVariant.priceVariant),
        description: description || undefined,
        additionalInformationText,
        additionalInformationTiles,
        // Country-restriction config on the referenced product.
        topics: extractTopicPaths(firstProduct),
        displayMode: extractAliasedDisplayMode(firstProduct),
      };
    });

    const notNullProductAlternativeModels = productAlternativeModels
      ? (productAlternativeModels.filter((model) => !!model) as ProductAlternativeModel[])
      : undefined;

    return notNullProductAlternativeModels;
  }

  private parseShopFeatures(
    shopFeatures: ShopFeaturesFragment | null | undefined,
  ): ParsedShopFeatures {
    const items = shopFeatures?.items;

    const parsedShopFeatures = items?.map((item) => {
      const icon = extractComponentFromChunk(item.components, 'icon', 'ImageContent');
      const text = extractComponentFromChunk(item.components, 'text', 'SingleLineContent');

      if (!icon || !text) {
        return null;
      }

      return {
        icon,
        text,
      };
    });

    return parsedShopFeatures;
  }

  private extractUniqueColors(variants: V[]): ColorDetail[] {
    const uniqueColors = new Map<string, ColorDetail>();
    variants.forEach((variant) => {
      if (variant.color && !uniqueColors.has(variant.color.name)) {
        uniqueColors.set(variant.color.name, variant.color);
      }
    });
    return Array.from(uniqueColors.values());
  }

  /**
   * Parses a variant-level item-relation component (e.g. `cylinder` or
   * `attribute-wildcard`) into a tile detail.
   */
  protected parseRelationTileDetail(
    variantComponents: ProductVariantComponentsFragment[] | null | undefined,
    componentId: string,
  ): AttributeTileDetail | undefined {
    return parseRelationTileDetail(
      componentByTypename(
        variantComponents?.find((component) => component.id === componentId)?.content,
        'ItemRelationsContent',
      ),
    );
  }

  /**
   * Extracts the unique set of relation tiles (cylinders, wildcard options, …)
   * across all variants, keyed by their `type`.
   */
  protected extractUniqueRelationTiles(
    variants: V[],
    selector: (variant: V) => AttributeTileDetail | undefined,
  ): AttributeTileDetail[] {
    const uniqueTiles = new Map<string, AttributeTileDetail>();
    variants.forEach((variant) => {
      const tile = selector(variant);
      if (tile && !uniqueTiles.has(tile.type)) {
        uniqueTiles.set(tile.type, tile);
      }
    });
    return Array.from(uniqueTiles.values());
  }
}
